import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin, TrendingUp, TrendingDown, Printer, BarChart3, Star, Link2, Link2Off, RefreshCw, Edit3 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Label, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { TimezoneSelect, tzLabel } from "../../components/TimezoneSelect";
import { DEFAULT_TZ, setTimeZones } from "../../lib/time";

const EMPTY = { name: "", city: "", province: "", region_id: "", address: "", timezone: "" };

function OpeningHours({ loc, onSaved }) {
  const [h, setH] = useState({ open: loc.opening_hours?.open || "", close: loc.opening_hours?.close || "" });
  const dirty = h.open !== (loc.opening_hours?.open || "") || h.close !== (loc.opening_hours?.close || "");
  const save = async () => {
    try { await api.patch(`/locations/${loc.id}`, { name: loc.name, opening_hours: h }); toast.success("Opening hours saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-3" data-testid={`opening-hours-${loc.id}`}>
      <p className="mb-1 text-xs uppercase tracking-wide text-slate-400">Opening hours (daily)</p>
      <div className="flex items-center gap-1.5">
        <Input type="time" className="h-8 text-xs" value={h.open} onChange={(e) => setH({ ...h, open: e.target.value })} data-testid={`open-${loc.id}`} />
        <span className="text-xs text-slate-400">to</span>
        <Input type="time" className="h-8 text-xs" value={h.close} onChange={(e) => setH({ ...h, close: e.target.value })} data-testid={`close-${loc.id}`} />
        {dirty && <Button size="sm" className="h-8 bg-[#4338CA] px-3 text-xs hover:bg-[#3730A3]" onClick={save} data-testid={`save-hours-${loc.id}`}>Save</Button>}
      </div>
    </div>
  );
}

export default function Locations() {
  const [dash, setDash] = useState(null);
  const [org, setOrg] = useState(null);
  const [regions, setRegions] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [renameLoc, setRenameLoc] = useState(null); // { id, name } when open
  const [renameName, setRenameName] = useState("");
  const [renaming, setRenaming] = useState(false);

  const load = () => api.get("/dashboard/corporate").then((r) => setDash(r.data)).catch(() => {});
  const orgTz = org?.timezone || DEFAULT_TZ;
  const changeTz = async (loc, tz) => {
    try {
      const { data } = await api.patch(`/locations/${loc.id}`, { name: loc.name, timezone: tz });
      setTimeZones(orgTz, [data]);
      toast.success(`${loc.name} now uses ${tzLabel(tz)}`); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const openRename = (loc) => { setRenameLoc(loc); setRenameName(loc.name || ""); };
  const closeRename = () => { setRenameLoc(null); setRenameName(""); };
  const saveRename = async () => {
    const name = renameName.trim();
    if (!renameLoc || !name || name === renameLoc.name) { closeRename(); return; }
    setRenaming(true);
    try {
      await api.patch(`/locations/${renameLoc.id}`, { name });
      toast.success("Location renamed");
      closeRename();
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setRenaming(false); }
  };
  useEffect(() => {
    load();
    api.get("/organization").then((r) => setOrg(r.data.organization)).catch(() => {});
    api.get("/regions").then((r) => setRegions(r.data)).catch(() => {});
  }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const create = async () => {
    try {
      const payload = { ...form, timezone: form.timezone || orgTz };
      if (!payload.region_id) delete payload.region_id;
      const { data } = await api.post("/locations", payload);
      setTimeZones(orgTz, [data]);
      toast.success("Location created"); setOpen(false);
      setForm(EMPTY); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const ranking = dash?.ranking || [];

  return (
    <div className="space-y-6" data-testid="locations-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Label>{org?.name || "Network"}</Label>
          <h1 className="font-head text-2xl font-bold text-slate-900 sm:text-3xl">Locations</h1>
          <p className="mt-1 text-sm text-slate-500">Performance dashboard for every store in your organisation.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-location-button" className="bg-[#4338CA] hover:bg-[#3730A3]">
          <Plus className="mr-1 h-4 w-4" /> Add location
        </Button>
      </div>

      {/* org KPI strip */}
      {dash && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="locations-kpis">
          <Kpi label="Service Index" value={dash.org_score?.ssi ?? "—"} accent />
          <Kpi label="Locations" value={dash.totals?.locations ?? 0} />
          <Kpi label="Active staff" value={dash.totals?.employees ?? 0} />
          <Kpi label="Verified ratings" value={dash.totals?.verified_interactions ?? 0} />
        </div>
      )}

      {ranking.length === 0 ? (
        <EmptyState icon={MapPin} title="No locations yet" hint="Create your first location to start collecting feedback." />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {ranking.map((c) => (
            <div key={c.location.id} className="flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`location-${c.location.id}`}>
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <p className="font-head text-lg font-semibold text-slate-900 truncate">{c.location.name}</p>
                    <button
                      type="button"
                      onClick={() => openRename(c.location)}
                      title="Rename location"
                      aria-label="Rename location"
                      data-testid={`rename-location-${c.location.id}`}
                      className="rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-indigo-600"
                    >
                      <Edit3 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <p className="text-sm text-slate-500">{[c.location.city, c.location.province].filter(Boolean).join(", ") || "—"}</p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-2 py-1 text-sm font-bold text-indigo-700">
                  <Star className="h-3.5 w-3.5" /> {c.score?.ssi || 0}
                </span>
              </div>
              <GooglePill google={c.location.google} locationId={c.location.id} onChanged={load} />
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Ratings</p>
                  <p className="font-semibold text-slate-800">{c.score?.verified_interactions ?? 0}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">30-day trend</p>
                  <p className={`inline-flex items-center gap-1 font-semibold ${c.trend_pct >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {c.trend_pct >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                    {c.trend_pct > 0 ? "+" : ""}{c.trend_pct}%
                  </p>
                </div>
              </div>
              {c.open_cases > 0 && (
                <p className="mt-3 rounded-lg bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">{c.open_cases} open recovery case(s)</p>
              )}
              <div className="mt-3">
                <p className="mb-1 text-xs uppercase tracking-wide text-slate-400">Time zone</p>
                <TimezoneSelect value={c.location.timezone || orgTz} onChange={(tz) => changeTz(c.location, tz)}
                  testId={`location-tz-${c.location.id}`} className="h-8 text-xs" />
              </div>
              <OpeningHours loc={c.location} onSaved={load} />
              <div className="mt-4 flex gap-2 border-t border-slate-100 pt-3">
                <Link to={`/app/store?location=${c.location.id}`} className="flex-1" data-testid={`view-dashboard-${c.location.id}`}>
                  <Button variant="outline" size="sm" className="w-full"><BarChart3 className="mr-1 h-4 w-4" /> Dashboard</Button>
                </Link>
                <Link to={`/app/print/location/${c.location.id}`} data-testid={`print-location-${c.location.id}`}>
                  <Button variant="outline" size="sm"><Printer className="h-4 w-4" /></Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add location</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <Input placeholder="Location name" value={form.name} onChange={set("name")} data-testid="loc-name" />
            <Input placeholder="City" value={form.city} onChange={set("city")} data-testid="loc-city" />
            <Input placeholder="Province" value={form.province} onChange={set("province")} />
            <TimezoneSelect value={form.timezone || orgTz} onChange={set("timezone")} testId="loc-timezone" />
            {regions.length > 0 && (
              <Select value={form.region_id} onValueChange={set("region_id")}>
                <SelectTrigger><SelectValue placeholder="Region (optional)" /></SelectTrigger>
                <SelectContent>{regions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={create} disabled={!form.name} data-testid="save-location" className="bg-[#4338CA] hover:bg-[#3730A3]">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!renameLoc} onOpenChange={(v) => { if (!v) closeRename(); }}>
        <DialogContent data-testid="rename-location-dialog">
          <DialogHeader><DialogTitle>Rename location</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <p className="text-xs text-slate-500">New name for <span className="font-semibold text-slate-700">{renameLoc?.name}</span></p>
            <Input
              value={renameName}
              onChange={(e) => setRenameName(e.target.value)}
              placeholder="Location name"
              data-testid="rename-location-input"
              onKeyDown={(e) => e.key === "Enter" && saveRename()}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRename}>Cancel</Button>
            <Button
              onClick={saveRename}
              disabled={renaming || !renameName.trim() || renameName.trim() === renameLoc?.name}
              data-testid="rename-location-save"
              className="bg-[#4338CA] hover:bg-[#3730A3]"
            >
              {renaming ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Kpi({ label, value, accent }) {
  return (
    <div className={`rounded-xl border p-4 ${accent ? "border-indigo-200 bg-indigo-50" : "border-slate-200 bg-white"}`}>
      <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
      <p className={`mt-1 font-head text-2xl font-bold ${accent ? "text-indigo-700" : "text-slate-900"}`}>{value}</p>
    </div>
  );
}

function GooglePill({ google, locationId, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualValue, setManualValue] = useState("");

  const match = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/locations/${locationId}/google/match`);
      toast.success(`Linked to ${data.display_name || "Google place"}`);
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const refresh = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/locations/${locationId}/google/refresh`);
      toast.success("Benchmarks refreshed");
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const unlink = async () => {
    if (!window.confirm("Unlink this location from Google?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/locations/${locationId}/google/unlink`);
      toast.success("Unlinked");
      onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };
  const manualMatch = async () => {
    if (!manualValue.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/locations/${locationId}/google/manual-match`, { place_id: manualValue.trim() });
      toast.success(`Linked to ${data.display_name || "Google place"}`);
      setManualOpen(false);
      setManualValue("");
      onChanged && onChanged();
    } catch (e) { toast.error(`Could not link to Google — ${apiError(e)}`); }
    finally { setBusy(false); }
  };

  const ManualDialog = (
    <Dialog open={manualOpen} onOpenChange={setManualOpen}>
      <DialogContent data-testid="google-manual-dialog">
        <DialogHeader>
          <DialogTitle>Link this store to Google manually</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <p className="text-sm text-slate-600">
            Open your store on Google Maps, tap <b>Share → Copy link</b>, and paste it below. Or paste a Place ID starting with <code>ChIJ…</code>.
          </p>
          <Input
            value={manualValue}
            onChange={(e) => setManualValue(e.target.value)}
            placeholder="https://maps.app.goo.gl/… or ChIJ…"
            data-testid="google-manual-input"
          />
          <p className="text-xs text-slate-500">Tip: shortened <code>maps.app.goo.gl</code> links work only after you open them once so Google expands them; paste the long URL for best results.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setManualOpen(false)}>Cancel</Button>
          <Button onClick={manualMatch} disabled={busy || !manualValue.trim()} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="google-manual-save">
            {busy ? "Linking…" : "Link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (!google?.place_id) {
    return (
      <>
        {ManualDialog}
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <div className="inline-flex items-center gap-1 rounded-full border border-dashed border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-500" data-testid="google-pill-unlinked">
            <span className="h-1.5 w-1.5 rounded-full bg-slate-300" /> Not linked to Google
          </div>
          <button
            type="button"
            onClick={match}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[11px] font-semibold text-indigo-700 transition hover:bg-indigo-50 disabled:opacity-50"
            data-testid={`google-link-${locationId}`}
          >
            <Link2 className="h-3 w-3" /> {busy ? "Linking…" : "Auto-link"}
          </button>
          <button
            type="button"
            onClick={() => setManualOpen(true)}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 transition hover:bg-slate-50"
            data-testid={`google-manual-${locationId}`}
          >
            <Edit3 className="h-3 w-3" /> Paste link
          </button>
        </div>
      </>
    );
  }
  const rating = google.rating;
  const count = google.user_rating_count;
  return (
    <>
      {ManualDialog}
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <div
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800"
          title={google.display_name || "Linked to Google"}
          data-testid="google-pill-linked"
        >
          <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
          <span>{rating != null ? Number(rating).toFixed(1) : "—"}</span>
          {count != null && <span className="font-normal text-amber-700">· {count.toLocaleString()}</span>}
          <span className="font-normal text-amber-700">on Google</span>
        </div>
        {google.display_name && (
          <span className="truncate text-[11px] text-slate-500" title={google.display_name}>
            {google.display_name}
          </span>
        )}
        <button
          type="button"
          onClick={() => setManualOpen(true)}
          disabled={busy}
          title="Not the right business? Paste the correct link"
          className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 text-slate-500 transition hover:bg-slate-50 disabled:opacity-50"
          data-testid={`google-fix-${locationId}`}
        >
          <Edit3 className="h-3 w-3" />
        </button>
        <button
          type="button"
          onClick={refresh}
          disabled={busy}
          title="Refresh"
          className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 text-slate-500 transition hover:bg-slate-50 disabled:opacity-50"
          data-testid={`google-refresh-${locationId}`}
        >
          <RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} />
        </button>
        <button
          type="button"
          onClick={unlink}
          disabled={busy}
          title="Unlink"
          className="inline-flex items-center rounded-full border border-slate-200 bg-white p-1 text-slate-500 transition hover:bg-rose-50 hover:text-rose-600 disabled:opacity-50"
          data-testid={`google-unlink-${locationId}`}
        >
          <Link2Off className="h-3 w-3" />
        </button>
      </div>
    </>
  );
}
